import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { PLATFORM_LABEL, api, compact, navigate, nf, useApi } from '../lib.jsx';
import { Combobox } from '../ui.jsx';
import { accountIdeas, accountLabel, aliasGroups, exclusionIdeas, namesTarget, questionIdeas, tagsToCheck } from '../suggest.js';

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => iso(new Date(Date.now() - n * 864e5));
const RANGES = [{ id: '7', label: 'Last 7 days' }, { id: '30', label: 'Last 30 days' }, { id: '90', label: 'Last 90 days' }, { id: 'custom', label: 'Custom' }];
const LANG_CODES = ['en', 'es', 'pt', 'hi', 'id', 'fr', 'de', 'ar'];
const langName = (c) => { try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(c); } catch { return c; } };
const MODE_OPTIONS = [{ id: 'understand', label: 'Understand a brand, product or topic' }, { id: 'compare', label: 'Compare two' }];
const emptyTarget = () => ({ name: '', aliases: [], context: [], exclusions: [], accounts: [], postUrls: '' });
const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export function ChipsInput({ values, onChange, placeholder, label, max = 10, format = (v) => v }) {
  const [draft, setDraft] = useState('');
  const add = (v) => {
    const parts = v.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length) onChange([...new Set([...values, ...parts])].slice(0, max));
    setDraft('');
  };
  return (
    <div className="chips-input">
      {values.map((v) => (
        <span className="chip" key={v}>{format(v)}<button type="button" aria-label={`Remove ${format(v)}`} onClick={() => onChange(values.filter((x) => x !== v))}>×</button></span>
      ))}
      <input type="text" aria-label={label} value={draft} placeholder={values.length ? '' : placeholder}
        onChange={(e) => (e.target.value.endsWith(',') ? add(e.target.value) : setDraft(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); add(draft); }
          if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1));
        }}
        onBlur={() => draft && add(draft)} />
    </div>
  );
}

export function Seg({ value, options, onChange, label }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => <button type="button" key={o.id} aria-pressed={value === o.id} onClick={() => onChange(o.id)}>{o.label}</button>)}
    </div>
  );
}

// ---------- suggestions (server/suggest.ts; grouping in ../suggest.js) ----------

const fold = (s) => s.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();
const plural = (n, w) => `${nf.format(n)} ${w}${n === 1 ? '' : 's'}`;

/** Names researched before in this workspace that contain what was typed, those starting with it first. */
function pastNames(history, q) {
  const k = fold(q);
  return history.filter((h) => fold(h.name).includes(k))
    .sort((a, b) => fold(b.name).startsWith(k) - fold(a.name).startsWith(k)).slice(0, 4)
    .map((h) => ({ key: `past:${h.name.toLowerCase()}`, label: h.name, qid: null, detail: h.runs > 1 ? `Researched ${h.runs} times before` : 'Researched before' }));
}

const nameCache = new Map(); // for as long as the page is open: backspacing does not ask again
/** Past names first (with a Wikidata id when Wikipedia has the same name), then Wikipedia and Wikidata. */
async function findNames(history, q) {
  const k = q.toLowerCase();
  let r = nameCache.get(k);
  if (!r) { r = await api(`/api/suggest/names?q=${encodeURIComponent(q)}`); if (!r.cooldown) nameCache.set(k, r); }
  const past = pastNames(history, q);
  const wiki = new Map(r.results.map((x) => [x.label.toLowerCase(), x]));
  return [
    ...past.map((p) => ({ ...p, qid: wiki.get(p.label.toLowerCase())?.qid ?? null })),
    ...r.results.filter((x) => !past.some((p) => p.label.toLowerCase() === x.label.toLowerCase())).map((x) => ({
      key: `wiki:${x.qid ?? x.label}`, label: x.label, qid: x.qid, via: x.via,
      detail: [x.via ? `Also called ${x.via}` : null, x.description].filter(Boolean).join(' · ') || null,
    })),
  ];
}

/**
 * Suggestions for one name, asked again (after a pause) when the name, its spellings or the market change: tags
 * and accounts from past research, then AI ideas. Older answers stay on screen until newer ones arrive, unless
 * the name itself changed. Facts come from Wikidata once a name with a Wikidata id is picked.
 */
function useSuggestions(name, aliases, market, qid, llm) {
  const [rel, setRel] = useState(null);
  const [ai, setAi] = useState(null);
  const [facts, setFacts] = useState(null);
  const [loading, setLoading] = useState(null); // 'related' | 'ideas'
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState({}); // Instagram counts by tag
  const [check, setCheck] = useState(null); // whether "Check on Instagram" is available
  const [checking, setChecking] = useState(false);
  const [checkNote, setCheckNote] = useState(null);
  const [tried, setTried] = useState([]); // checked without an answer: the next check moves on
  const seq = useRef(0);
  const merge = (c) => { if (c && Object.keys(c).length) setChecked((x) => ({ ...x, ...c })); };

  useEffect(() => {
    const n = ++seq.current;
    if (name.length < 2) { setLoading(null); return; }
    setLoading('related');
    const body = { name, aliases, market };
    const t = setTimeout(async () => {
      try {
        const r = await api('/api/suggest/related', { body });
        if (n !== seq.current) return;
        setRel({ ...r, for: name }); setCheck(r.check); merge(r.checked); setError(null);
        if (!llm) return setLoading(null);
        setLoading('ideas');
        const seen = [...r.seen, ...r.variants].map((x) => x.tag).slice(0, 15);
        const i = await api('/api/suggest/ideas', { body: { ...body, seen } }).catch((e) => ({ failed: e.message }));
        if (n !== seq.current) return;
        if (i.failed) setAi((old) => ({ ...(old?.for === name ? old : { ideas: [], exclusions: [] }), for: name, error: i.failed }));
        else { setAi({ ...i, for: name }); merge(i.checked); }
        setLoading(null);
      } catch (e) { if (n === seq.current) { setError(e.message); setLoading(null); } }
    }, 500);
    return () => clearTimeout(t);
  }, [name, aliases.join('\n'), market, llm]);

  useEffect(() => {
    setFacts(null);
    if (!qid) return;
    let live = true;
    api(`/api/suggest/entity?qid=${encodeURIComponent(qid)}&market=${encodeURIComponent(market)}`).then((f) => live && setFacts(f), () => {});
    return () => { live = false; };
  }, [qid, market]);

  async function runCheck(tags) {
    setChecking(true); setCheckNote(null);
    try {
      const r = await api('/api/suggest/check', { body: { tags } });
      merge(r.results); setCheck(r.check); setTried((x) => [...x, ...tags]);
      const got = tags.filter((x) => !r.unknown.includes(x));
      setCheckNote({ text: [got.length ? `Checked ${listTags(got)} on Instagram.` : null, r.unknown.length ? `Instagram gave no count for ${listTags(r.unknown)}.` : null].filter(Boolean).join(' ') });
    } catch (e) { setCheckNote({ text: e.message, error: true }); }
    setChecking(false);
  }

  const mine = (x) => (x?.for === name ? x : null);
  return { rel: mine(rel), ai: mine(ai), facts, loading, error, checked, check, checking, checkNote, tried, runCheck };
}

/** One suggestion: a button that adds it. Nothing is added without a click. */
function Add({ label, note, why, onAdd, disabled }) {
  return (
    <button type="button" className="chip suggest" onClick={onAdd} disabled={disabled} title={why || undefined}>
      <span aria-hidden="true">+</span><span className="sr-only">Add</span> {label}{note ? <>{' '}<span className="sg-n">{note}</span></> : null}
    </button>
  );
}

function Group({ title, hint, children }) {
  const id = useId();
  return (
    <div className="sg-group" role="group" aria-labelledby={id}>
      <p className="sg-title"><span id={id}>{title}</span>{hint ? <>{' '}<span className="sg-hint">{hint}</span></> : null}</p>
      <div className="sg-chips">{children}</div>
    </div>
  );
}

/** What a suggested tag or name shows next to it: its Instagram count once checked, else posts in past research, else where it came from. */
const tagNote = (x) => (x.ig != null ? `${compact(x.ig)} on Instagram` : x.posts ? plural(x.posts, 'post') : x.source ?? null);
const listTags = (tags) => { const t = tags.map((x) => `#${x}`); return t.length > 1 ? `${t.slice(0, -1).join(', ')} and ${t.at(-1)}` : t[0]; };

function accountWhy(a, past, marketName) {
  if (a.seen) {
    const s = a.seen;
    const bits = [s.verified ? 'verified' : null, s.followers != null ? `${compact(s.followers)} followers` : null, `${plural(s.posts, 'post')} in your past research`, s.market ? `may be the ${marketName} account` : null];
    const text = bits.filter(Boolean).join(', ');
    return text[0].toUpperCase() + text.slice(1);
  }
  if (a.wikidata) return `Listed on Wikidata: a global account, maybe not the one for ${marketName}`;
  return `Used before for ${past?.name}`;
}

const ASKS = ['ambiguous', 'unreleased', 'not_found', 'website', 'no_site'];
/** The name check's follow-up question, until the user picks a meaning or a correction, or confirms the name as typed. */
export const nameBlocks = (c, t) => !!c && c.name === t.name.trim() && ASKS.includes(c.status) && !t.confirmed;
const plainLabel = (l) => l.replace(/\s+\([^)]*\)$/, ''); // "Dove (Unilever brand)" → "Dove"

const handleOf = (a) => `${PLATFORM_LABEL[a.split(':')[0]] ?? a.split(':')[0]} ${a.startsWith('linkedin:') ? a.split(':')[1] : `@${a.split(':')[1]}`}`;

function NameCheck({ c, t, onPick, onConfirm, onSite }) {
  const name = t.name.trim();
  if (!c || c.name !== name) return null;
  if (c.status === 'ok') return c.about ? <p className="hint name-ok">✓ {c.about}</p> : null;
  // A web address: research what the site is about, by its name, with the accounts the site links to.
  if (c.status === 'website') return (
    <div className="name-check" role="alert">
      <p className="name-check-head">This is the website of <strong>{c.label}</strong>{c.description ? <>: {c.description}</> : '.'}</p>
      {c.site.accounts.length ? <p className="small">Its accounts are added too: {c.site.accounts.map(handleOf).join(', ')}.</p> : null}
      <div><button type="button" className="btn small primary" onClick={() => onSite(c)}>Research {c.label}</button></div>
    </div>
  );
  if (c.status === 'no_site') return <div className="name-check" role="alert"><p className="name-check-head">“{name}” looks like a web address, but the website couldn’t be opened. Check the address, or type the name of the brand, product or topic.</p></div>;
  if (!ASKS.includes(c.status)) return null;
  const head = c.status === 'ambiguous' ? <>“{name}” can mean several things. Which one do you mean?</>
    : c.status === 'unreleased' ? <>“{name}” hasn’t been released yet{c.description ? <> (Wikidata: “{c.description}”)</> : null}. Posts about it are mostly leaks and rumours.</>
    : <>“{name}” wasn’t found on Wikipedia or Wikidata. Check the spelling.</>;
  const anyway = c.status === 'ambiguous' ? `None of these: research “${name}” as typed` : c.status === 'unreleased' ? `Research “${name}” anyway`
    : `“${name}” is right (a specific product, a small brand or a topic): research it anyway`;
  return (
    <div className={`name-check${t.confirmed ? ' done' : ''}`} role={t.confirmed ? undefined : 'alert'}>
      <p className="name-check-head">{head}</p>
      {c.suggestions.length ? (
        <div className="name-options" role="group" aria-label={c.status === 'ambiguous' ? 'Meanings' : 'Did you mean'}>
          {c.status !== 'ambiguous' ? <span className="small">Did you mean:</span> : null}
          {c.suggestions.map((o) => (
            <button type="button" key={o.qid ?? o.label} className="name-option" onClick={() => onPick(o, c.status === 'ambiguous')}>
              <strong>{o.label}</strong>{o.description ? <span>{o.description}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
      <label className="check small"><input type="checkbox" checked={!!t.confirmed} onChange={(e) => onConfirm(e.target.checked)} /> {anyway}</label>
    </div>
  );
}

/** `named` is the name the suggestions are for: it is set on a pick, on Enter or on leaving the field, not per keystroke. */
function TargetCard({ n, t: raw, onChange, compare, market, marketName, history, llm, named, onNamed, check, onCheck }) {
  const t = { ...raw, context: raw.context ?? [] }; // drafts from before related topics
  const set = (k) => (v) => onChange({ ...t, [k]: v });
  const add = (k) => (v) => onChange({ ...t, [k]: [...t[k], v] });
  const nameId = useId();
  const [entity, setEntity] = useState(null); // a Wikipedia or Wikidata pick: { qid, via }
  const s = useSuggestions(named, t.aliases, market, entity?.qid, llm);
  // Does the settled name exist, which one is meant, and is it out? Free (Wikipedia and Wikidata); the server asks again.
  useEffect(() => {
    const q = named.trim();
    if (q.length < 2) { onCheck(null); return; }
    let live = true;
    api(`/api/suggest/verify?name=${encodeURIComponent(q)}${t.qid ? `&qid=${encodeURIComponent(t.qid)}` : ''}${t.site ? `&site=${encodeURIComponent(t.site)}` : ''}`).then((c) => live && onCheck(c), () => live && onCheck(null));
    return () => { live = false; };
  }, [named, t.qid, t.site]);
  // From a web address: the site's name, its domain as another name for it (a post linking it counts), its accounts.
  const fromSite = (c) => {
    const merge = (xs, add, max) => [...new Map([...xs, ...add].map((x) => [x.toLowerCase(), x])).values()].slice(0, max);
    onChange({ ...t, name: c.label, site: c.site.url, qid: undefined, confirmed: false, aliases: merge(t.aliases, [c.site.host], 10), accounts: merge(t.accounts, c.site.accounts, 5) });
    onNamed(c.label);
    setEntity(null);
  };
  // A correction replaces the name; a meaning of an ambiguous name keeps the words people use and records which one.
  const pickName = (o, keep) => {
    const name = keep ? t.name : plainLabel(o.label);
    onChange({ ...t, name, qid: o.qid ?? undefined, confirmed: false });
    if (!keep) onNamed(name);
    setEntity(o.qid ? { qid: o.qid, via: null } : null);
  };
  const past = history.find((h) => h.name.toLowerCase() === named.toLowerCase()) ?? null;
  const g = aliasGroups({ name: named, aliases: t.aliases, context: t.context, past, entity, facts: s.facts, rel: s.rel, ai: s.ai, checked: s.checked });
  const ex = exclusionIdeas({ name: named, aliases: t.aliases, exclusions: t.exclusions, past, rel: s.rel, ai: s.ai });
  const acc = accountIdeas({ accounts: t.accounts, past, rel: s.rel, facts: s.facts });
  const toCheck = tagsToCheck({ name: named, aliases: t.aliases, ideas: g.ideas.map((x) => x.value), checked: s.checked, tried: s.tried });
  const full = { aliases: t.aliases.length >= 10, context: t.context.length >= 10, exclusions: t.exclusions.length >= 10, accounts: t.accounts.length >= 5 };
  // A suggestion that names the target is an alias; any other (#skincare) is a related topic, which never counts a post.
  const field = (x) => (namesTarget(x.value, named, t.aliases) ? 'aliases' : 'context');
  const chip = (x) => <Add key={x.value} label={field(x) === 'context' ? `${x.value} (topic)` : x.value} note={tagNote(x)} why={x.why} disabled={full[field(x)]} onAdd={() => add(field(x))(x.value)} />;
  const aiChecked = g.ideas.some((x) => x.ig != null);
  // The box opens once there is something to show (a first lookup with nothing found shows nothing).
  const anything = Object.values(g).some((x) => x.length) || s.loading === 'ideas' || s.ai?.error || s.error || s.checkNote || (toCheck.length > 0 && s.check?.available);

  return (
    <fieldset className="target-card">
      <legend>{compare ? `Brand, product or topic ${n}` : 'Brand, product or topic'}</legend>
      <div className="field">
        <label className="label" htmlFor={nameId}>Name</label>
        <Combobox id={nameId} label="Name" value={t.name} maxLength={200} placeholder={n === 1 ? 'A brand, product, topic or website' : 'The one to compare with'}
          onChange={(v) => { setEntity(null); onChange({ ...t, name: v, qid: undefined, site: undefined, confirmed: false }); }}
          onPick={(o) => { onChange({ ...t, name: o.label, qid: o.qid ?? undefined, site: undefined, confirmed: false }); onNamed(o.label); setEntity(o.qid ? { qid: o.qid, via: o.via } : null); }}
          onBlur={() => onNamed(t.name.trim())} onKeyDown={(e) => e.key === 'Enter' && onNamed(t.name.trim())}
          local={(q) => pastNames(history, q)} load={(q) => findNames(history, q)} note="From your past research, Wikipedia and Wikidata" />
        <NameCheck c={check} t={t} onPick={pickName} onConfirm={(v) => onChange({ ...t, confirmed: v })} onSite={fromSite} />
      </div>
      <div className="field">
        <span className="label">Other names and spellings</span>
        <ChipsInput values={t.aliases} onChange={set('aliases')} label={`Aliases for ${t.name || `brand ${n}`}`} placeholder="Spellings, product lines, hashtags" />
        <span className="hint">A post counts if it mentions any of these, so add only names of {t.name || 'it'} itself. A hashtag that does not contain the name (#skincare) is moved to related topics. Press Enter after each one.</span>
        {named.length >= 2 && anything ? (
          <div className="sg">
            <p className="sg-head">
              <span className="sg-for">Suggestions for {named}</span>
              <span className="sg-status" role="status">{s.loading === 'related' ? 'Updating…' : s.loading === 'ideas' ? 'Getting AI ideas…' : ''}</span>
            </p>
            {s.error ? <p className="sg-msg">{s.error}</p> : null}
            {full.aliases ? <p className="sg-msg">That is the most this field takes. Remove one to add another.</p> : null}
            {g.names.length ? <Group title="Other names">{g.names.map(chip)}</Group> : null}
            {g.variants.length ? <Group title="Brand variants" hint="Hashtags that contain the name">{g.variants.map(chip)}</Group> : null}
            {g.seen.length ? <Group title="Seen with your tags in past research" hint="Posts that used both. Ones without the name are added as related topics.">{g.seen.map(chip)}</Group> : null}
            {llm && (g.ideas.length || s.loading === 'ideas' || s.ai?.error) ? (
              <Group title={aiChecked ? 'AI ideas' : 'AI ideas, not checked yet'} hint={aiChecked ? 'Instagram counts where checked' : null}>
                {g.ideas.map(chip)}
                {s.ai?.error ? <span className="sg-msg">{s.ai.error}</span> : !g.ideas.length ? <span className="sg-wait">Thinking of ideas…</span> : null}
              </Group>
            ) : null}
            {g.topics.length ? (
              <details className="sg-topics">
                <summary>Related topics <span className="sg-hint">({g.topics.length})</span></summary>
                <p className="sg-hint">These are added as related topics: they help tell posts apart but never count a post as {named} on their own.</p>
                <div className="sg-chips">{g.topics.map(chip)}</div>
              </details>
            ) : null}
            {toCheck.length && s.check ? (
              s.check.available ? (
                <div className="sg-check">
                  <button type="button" className="btn small" disabled={s.checking} onClick={() => s.runCheck(toCheck)}>{s.checking ? 'Checking…' : 'Check on Instagram'}</button>
                  <span className="sg-hint" role="status">{s.checking ? 'Asking Instagram. This takes about 20 seconds.' : `Post counts and related tags for ${listTags(toCheck)}. About 20 seconds, up to 0.02 credits. ${s.check.left} left today.`}</span>
                </div>
              ) : <p className="sg-hint">{s.check.reason}</p>
            ) : null}
            {s.checkNote ? <p className={s.checkNote.error ? 'sg-msg' : 'sg-hint'} role="status">{s.checkNote.text}</p> : null}
          </div>
        ) : null}
      </div>
      <div className="field">
        <span className="label">Related topics (optional)</span>
        <ChipsInput values={t.context} onChange={set('context')} label={`Related topics for ${t.name || `brand ${n}`}`} placeholder="e.g. #skincare, sensitive skin" />
        <span className="hint">The category or themes around {t.name || 'it'}. Not searched on their own, and a post about only these does not count: they help the screening tell relevant posts apart.</span>
      </div>
      <div className="field">
        <span className="label">Leave out posts mentioning (optional)</span>
        <ChipsInput values={t.exclusions} onChange={set('exclusions')} label={`Exclusions for ${t.name || `brand ${n}`}`} placeholder="e.g. giveaway" />
        {ex.length ? (
          <div className="sg">
            <ul className="sg-rows" aria-label={`Exclusions to consider for ${named}`}>
              {ex.map((x) => (
                <li key={x.value}>
                  <Add label={x.value} disabled={full.exclusions} onAdd={() => add('exclusions')(x.value)} />
                  <span className="sg-why">{x.ai ? `AI idea, not checked: ${x.why}` : x.why}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      <details className="more">
        <summary>Known accounts and posts (optional){acc.length ? <>{' '}<span className="sg-count">{acc.length} suggested</span></> : null}</summary>
        <div className="field">
          <span className="label">Account handles</span>
          <ChipsInput values={t.accounts} onChange={set('accounts')} max={5} format={accountLabel} label={`Accounts for ${t.name || `brand ${n}`}`} placeholder="@handle" />
          <span className="hint">The brand's own TikTok or Instagram handle, or a creator central to the topic. These usually carry the deepest discussion. A plain handle is tried on every platform; to keep one to a platform, prefix it: x:handle, threads:handle, facebook:PageName, linkedin:company-name.</span>
          {acc.length ? (
            <div className="sg">
              <ul className="sg-rows" aria-label={`Accounts to consider for ${named}`}>
                {acc.map((a) => (
                  <li key={a.value}>
                    <Add label={accountLabel(a.value)} disabled={full.accounts} onAdd={() => add('accounts')(a.value)} />
                    <span className="sg-why">{accountWhy(a, past, marketName)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
        <label className="field">Post links, one per line
          <textarea rows={3} value={t.postUrls} onChange={(e) => set('postUrls')(e.target.value)} placeholder="https://www.tiktok.com/@…/video/…, a YouTube video, an X or Facebook post, or an Amazon product page" />
        </label>
      </details>
    </fieldset>
  );
}

const MODE_LABEL = { keyword: 'keyword', hashtag: '#', profile: '@', url: 'link', web: 'web search' };
const searchLabel = (s) => (s.mode === 'hashtag' ? `#${s.value}` : s.mode === 'profile' ? `@${s.value}` : s.mode === 'url' ? 'post link' : s.mode === 'web' ? `web: "${s.value}"` : `"${s.value}"`);

/** Scope summary shown before Run, and again on the research page. */
export function ScopeSummary({ plan }) {
  const t = plan.totals;
  const byPlatform = useMemo(() => {
    const m = new Map();
    for (const e of plan.entries) {
      const cur = m.get(e.platform) ?? { missing: new Map(), notes: new Set() };
      for (const x of e.missing) cur.missing.set(x.mode, x.reason);
      for (const n of e.notes) cur.notes.add(n);
      m.set(e.platform, cur);
    }
    return m;
  }, [plan]);
  return (
    <div className="scope">
      <p className="scope-copy">{plan.copy}</p>
      <div className="table-wrap"><table className="data">
        <thead><tr><th>Researching</th><th>Platform</th><th>Searches</th><th className="num">Collect up to</th></tr></thead>
        <tbody>{plan.entries.map((e) => (
          <tr key={e.targetId + e.platform}>
            <td>{e.targetName}</td>
            <td>{PLATFORM_LABEL[e.platform]}</td>
            <td><span className="search-chips">{e.searches.map((s, i) => <span key={i} className="tag" title={MODE_LABEL[s.mode]}>{searchLabel(s)}</span>)}</span></td>
            <td className="num">{e.platform === 'amazon' ? <>{nf.format(e.limits.posts)} products<br /><span className="muted small">{nf.format(e.limits.comments)} reviews, up to {e.limits.commentsPerPost} per product</span></> : <>{nf.format(e.limits.posts)} posts<br /><span className="muted small">{nf.format(e.limits.comments)} comments, up to {e.limits.commentsPerPost} per post, replies included</span></>}</td>
          </tr>
        ))}</tbody>
      </table></div>
      <p className="small">Overall: up to <strong>{nf.format(t.overall)}</strong> posts and comments, replies included. Each one researched has its own allowance. Limits are "up to": screening and duplicates can lower the counts, and low-volume brands may not reach them.</p>
      <div className="scope-grid">
        {[...byPlatform].map(([p, x]) => (
          <div key={p} className="scope-platform">
            <h3>{PLATFORM_LABEL[p]}</h3>
            {x.missing.size ? <ul className="plain-list small">{[...x.missing].map(([mode, reason]) => <li key={mode}><strong>{mode}:</strong> {reason}</li>)}</ul> : null}
            {x.notes.size ? <ul className="plain-list small muted">{[...x.notes].map((n) => <li key={n}>{n}</li>)}</ul> : null}
          </div>
        ))}
      </div>
      {plan.unavailable?.length ? <p className="small warn-box">Left out of this run: {plan.unavailable.map((u) => `${PLATFORM_LABEL[u.platform] ?? u.platform} (${u.reason})`).join(', ')}</p> : null}
      <p className="small"><strong>Context processing:</strong> {plan.context}</p>
    </div>
  );
}

export function NewResearch({ cfg }) {
  // A platform with a note for a market (TikTok is blocked in India) starts unticked there; the user can tick it.
  const noted = (m) => cfg.marketNotes?.[m] ?? {};
  const [mode, setMode] = useState('understand');
  const [targets, setTargets] = useState([emptyTarget(), emptyTarget()]);
  const [named, setNamed] = useState(['', '']); // settled names, for suggestions and question ideas
  const [checks, setChecks] = useState([null, null]); // the name check of each settled name
  const [question, setQuestion] = useState('');
  const [platforms, setPlatforms] = useState(cfg.platforms.filter((p) => ['tiktok', 'instagram'].includes(p.id) && !noted(cfg.markets[0]?.code)[p.id]).map((p) => p.id));
  const [range, setRange] = useState('30');
  const [since, setSince] = useState(daysAgo(30));
  const [until, setUntil] = useState(daysAgo(0));
  const [market, setMarket] = useState(cfg.markets[0]?.code ?? '');
  const [languages, setLanguages] = useState([]);
  const [preset, setPreset] = useState('quick');
  const [adv, setAdv] = useState({ posts: 25, comments: 500, commentsPerPost: 30 });
  const [scope, setScope] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [strictMarket, setStrictMarket] = useState(false);
  const key = useRef(newKey()); // one key per form: a double-click or retry cannot create two runs
  const history = useApi('/api/suggest/history').data?.targets ?? []; // names researched before, filtered as you type
  const marketName = cfg.markets.find((c) => c.code === market)?.name ?? market;
  const pickMarket = (m) => { setMarket(m); setPlatforms((ps) => ps.filter((p) => !noted(m)[p])); };

  useEffect(() => {
    if (range === 'custom') return;
    setSince(daysAgo(Number(range))); setUntil(daysAgo(0));
  }, [range]);

  const active = mode === 'compare' ? targets : targets.slice(0, 1);
  const brief = {
    mode, question: question.trim(), platforms, since, until, market, strictMarket: strictMarket && market !== 'GLOBAL', languages, preset, limits: preset === 'advanced' ? adv : undefined,
    targets: active.map((t) => ({ ...t, postUrls: t.postUrls.split(/\s+/).map((s) => s.trim()).filter(Boolean) })),
  };
  const ready = active.every((t) => t.name.trim()) && platforms.length && market;

  useEffect(() => {
    setError(null); // a refusal is about the brief as it was
    if (!ready) { setScope(null); return; }
    const t = setTimeout(() => api('/api/plan', { body: brief }).then(setScope).catch((e) => setScope({ problems: [e.message] })), 350);
    return () => clearTimeout(t);
  }, [JSON.stringify(brief)]);

  async function run() {
    if (busy) return;
    // A name filled in without leaving the field (autofill) is settled now, so its check and choices show under it.
    setNamed((ns) => ns.map((x, i) => (active[i] ? active[i].name.trim() : x)));
    setBusy(true); setError(null);
    try {
      const r = await api('/api/runs', { body: { idempotencyKey: key.current, brief } });
      navigate(`/runs/${r.id}`);
    } catch (e) { setError(e.message); setBusy(false); }
  }

  const problems = scope?.problems ?? [];
  const nameAsks = active.some((t, i) => nameBlocks(checks[i], t));
  const qIdeas = questionIdeas(mode, named.slice(0, active.length).filter(Boolean)).filter((q) => q !== question);
  const presetText = (p) => `${nf.format(cfg.presets[p].posts)} relevant posts and up to ${nf.format(cfg.presets[p].comments)} comments, per platform, for each one researched`;

  return (
    <div className="page narrow">
      <div className="new-head">
        <p className="eyebrow">New study</p>
        <h1>New <em>research</em></h1>
        <p className="muted">Tell us the brand, product or topic, or paste its website. We'll collect what people post and comment about it on TikTok, Instagram, Reddit, YouTube and LinkedIn, and what customers say in Amazon reviews.</p>
      </div>
      {cfg.synthetic ? <div className="demo-banner">Demo mode: research runs on synthetic posts and comments, so you can try the flow at no cost. Nothing real is collected.</div> : null}

      <section className="step">
        <div className="step-body">
          <div className="step-head">
            <p className="eyebrow"><span className="step-n">01</span>The subject</p>
            <h2>What are you researching?</h2>
          </div>
          <Seg value={mode} onChange={setMode} options={MODE_OPTIONS} label="Research type" />
          <div className={mode === 'compare' ? 'target-pair' : ''}>
            {active.map((t, i) => (
              <TargetCard key={i} n={i + 1} t={t} compare={mode === 'compare'} market={market} marketName={marketName} history={history} llm={cfg.llm}
                named={named[i]} onNamed={(v) => setNamed((ns) => ns.map((x, j) => (j === i ? v : x)))}
                check={checks[i]} onCheck={(c) => setChecks((cs) => cs.map((x, j) => (j === i ? c : x)))}
                onChange={(v) => setTargets((ts) => ts.map((x, j) => (j === i ? v : x)))} />
            ))}
          </div>
          {mode === 'compare' ? <p className="hint">Each is searched separately with its own allowance. A post does not need to mention both.</p> : null}
          <div className="field">
            <label className="field">Question (optional)
              <textarea className="question-input" value={question} maxLength={500} onChange={(e) => setQuestion(e.target.value)} placeholder={cfg.defaultQuestion} />
            </label>
            {qIdeas.length ? (
              <div className="sg-chips q-ideas" role="group" aria-label="Question ideas">
                {qIdeas.map((q) => <button key={q} type="button" className="chip suggest" onClick={() => setQuestion(q)}>{q}</button>)}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="step">
        <div className="step-body">
          <div className="step-head">
            <p className="eyebrow"><span className="step-n">02</span>Platforms and dates</p>
            <h2>Where and when?</h2>
          </div>
          <div className="platform-grid">
            {cfg.platforms.map((p) => {
              const on = platforms.includes(p.id), note = noted(market)[p.id];
              return (
                <label key={p.id} className="platform-opt" data-checked={on}>
                  <input type="checkbox" checked={on} aria-label={p.label} aria-describedby={note ? `note-${p.id}` : undefined} onChange={() => setPlatforms(on ? platforms.filter((x) => x !== p.id) : [...platforms, p.id])} />
                  <span className="name">{p.label}</span>
                  {p.description ? <span className="desc">{p.description}</span> : null}
                  {note ? <span className="note" id={`note-${p.id}`}>{note}</span> : null}
                </label>
              );
            })}
          </div>
          <div className="field">
            <span className="label">Time period</span>
            <Seg value={range} onChange={setRange} options={RANGES} label="Time period" />
            {range === 'custom' ? (
              <div className="row2" style={{ marginTop: 8 }}>
                <label className="field">From<input type="date" value={since} max={until} onChange={(e) => setSince(e.target.value)} /></label>
                <label className="field">To<input type="date" value={until} min={since} max={daysAgo(0)} onChange={(e) => setUntil(e.target.value)} /></label>
              </div>
            ) : null}
          </div>
          <div className="row2">
            <label className="field">Target market
              <select value={market} onChange={(e) => pickMarket(e.target.value)}>
                {cfg.markets.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
              <span className="hint">{cfg.marketCopy}</span>
              {market && market !== 'GLOBAL' ? <label className="check small"><input type="checkbox" checked={strictMarket} onChange={(e) => setStrictMarket(e.target.checked)} /> Only posts that show they are from {marketName}</label> : null}
              {strictMarket && market !== 'GLOBAL' ? <span className="hint">A post counts as from {marketName} when it carries a geotag, the creator’s country, a local currency, shop, place or script. Most posts carry none, so expect a much smaller sample.</span> : null}
            </label>
            <fieldset className="field langs">
              <legend className="label">Languages (optional)</legend>
              <div className="lang-grid">
                {LANG_CODES.map((c) => (
                  <label key={c} className="check"><input type="checkbox" checked={languages.includes(c)} onChange={() => setLanguages(languages.includes(c) ? languages.filter((x) => x !== c) : [...languages, c])} /> {langName(c)}</label>
                ))}
              </div>
              <span className="hint">Leave empty for any language. Language is not treated as proof of location.</span>
            </fieldset>
          </div>
        </div>
      </section>

      <section className="step">
        <div className="step-body">
          <div className="step-head">
            <p className="eyebrow"><span className="step-n">03</span>Depth</p>
            <h2>How much to collect?</h2>
          </div>
          <Seg value={preset} onChange={setPreset} options={[{ id: 'scan', label: 'Scan' }, { id: 'quick', label: 'Quick' }, { id: 'standard', label: 'Standard' }, { id: 'advanced', label: 'Advanced' }]} label="Depth" />
          {preset === 'scan' ? <p className="hint">What is being posted, in minutes: each platform's posts ranked by engagement, with up to {cfg.presets.scan.commentsPerPost} comments read on each of the {cfg.presets.scan.posts} most engaging. No AI checks and no report, so some posts may be off the topic.</p>
          : preset !== 'advanced' ? <p className="hint">{presetText(preset)}, with up to {cfg.presets[preset].commentsPerPost} comments per post (replies included). The plan below gives each platform's numbers.</p> : (
            <div className="row3">
              <label className="field">Relevant posts
                <input type="number" min={5} max={cfg.caps.posts} value={adv.posts} onChange={(e) => setAdv({ ...adv, posts: Number(e.target.value) })} />
                <span className="hint">Up to {nf.format(cfg.caps.posts)}</span>
              </label>
              <label className="field">Comments in total
                <input type="number" min={0} max={cfg.caps.comments} step={50} value={adv.comments} onChange={(e) => setAdv({ ...adv, comments: Number(e.target.value) })} />
                <span className="hint">Up to {nf.format(cfg.caps.comments)}, replies included</span>
              </label>
              <label className="field">Comments per post
                <input type="number" min={0} max={cfg.caps.commentsPerPost} value={adv.commentsPerPost} onChange={(e) => setAdv({ ...adv, commentsPerPost: Number(e.target.value) })} />
                <span className="hint">Up to {cfg.caps.commentsPerPost}</span>
              </label>
              <p className="hint" style={{ gridColumn: '1 / -1' }}>Per platform, for each one researched.</p>
            </div>
          )}
        </div>
      </section>

      {scope?.plan ? (
        <section className="panel">
          <h2>Before you run</h2>
          <ScopeSummary plan={scope.plan} />
          <p className="small"><strong>Credits:</strong> {scope.synthetic ? 'Demo data, no credits used.'
            : 'Taken from your balance as the research runs. You can stop it at any time.'}</p>
        </section>
      ) : null}


      <div className="launchbar">
        <div className="est">
          {scope?.plan
            ? <><strong>{scope.synthetic ? 'Free (demo data)' : 'Uses credits as it runs'}</strong>
              <span className="muted small">Up to {nf.format(scope.plan.totals.overall)} posts and comments</span></>
            : <span className="muted">Name what you're researching and pick a platform to see the scope.</span>}
          {problems.length ? <span className="small" style={{ color: 'var(--neg)' }}>{problems.join(' ')}</span> : null}
          {nameAsks ? <span className="small" style={{ color: 'var(--neg)' }}>Answer the question under the name first.</span> : null}
          {scope?.warnings?.length ? <span className="small" role="status" style={{ color: 'var(--warn-ink)' }}>⚠ {scope.warnings.join(' ')}</span> : null}
          {error ? <span className="small" style={{ color: 'var(--neg)' }} role="alert">{error}</span> : null}
        </div>
        <button className="btn primary" disabled={!ready || !scope?.plan || busy || problems.length > 0 || nameAsks} onClick={run}>{busy ? 'Starting…' : preset === 'scan' ? 'Run scan' : 'Run research'}</button>
      </div>
    </div>
  );
}

